import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { toast } from 'sonner'
import { api } from '../../../lib/api'
import { downloadXlsx } from '../../../lib/downloadXlsx'
import { Modal } from '../../ui/Modal'
import { QuickDateRange } from '../../ui/DateField'
import { numePerioada } from '../../ui/FiltruPerioada'

type Criteriu = 'incheiate' | 'desfasurate'

const CRITERII: { cheie: Criteriu; titlu: string; detaliu: string }[] = [
  {
    cheie: 'incheiate',
    titlu: 'Încheiate în perioadă',
    detaliu: 'după data la care au fost închise, cu raportul de închidere',
  },
  {
    cheie: 'desfasurate',
    titlu: 'Desfășurate în perioadă',
    detaliu: 'toate cele care au loc sau au avut loc atunci, închise sau nu',
  },
]

const zi = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`

/**
 * Exportul Excel din PR & Evenimente (Dorian, 30.10.2026: „evenimentele inchise pe luna in curs",
 * pentru raportare). Implicit: incheiate luna aceasta. Ce intra si cum se calculeaza banii e in
 * `apps/api/src/modules/campanii/export.service.ts`.
 */
export function ExportExcel({ onInchide }: { onInchide: () => void }) {
  const azi = new Date()
  const [criteriu, setCriteriu] = useState<Criteriu>('incheiate')
  const [de, setDe] = useState(zi(new Date(azi.getFullYear(), azi.getMonth(), 1)))
  const [pana, setPana] = useState(zi(new Date(azi.getFullYear(), azi.getMonth() + 1, 0)))
  const [anulate, setAnulate] = useState(false)
  const [descarca, setDescarca] = useState(false)

  const intoarsa = !!de && !!pana && de > pana
  const q = new URLSearchParams({ criteriu, de, pana, anulate: String(anulate) }).toString()

  // Cate intra, inainte de descarcare: omul vede ce primeste.
  const numar = useQuery({
    queryKey: ['campanii-export-numar', q],
    queryFn: async () => (await api.get<{ numar: number }>(`/campanii/export/numar?${q}`)).data,
    enabled: !intoarsa,
  })

  async function descarcaFisierul() {
    setDescarca(true)
    try {
      await downloadXlsx(`/campanii/export?${q}`, 'PR-Evenimente.xlsx')
      onInchide()
    } catch (e) {
      toast.error((e as Error).message || 'Exportul n-a mers. Încearcă din nou.')
    } finally {
      setDescarca(false)
    }
  }

  const n = numar.data?.numar
  const rezumat = intoarsa
    ? 'Data de sfârșit e înaintea celei de început.'
    : numar.isError
      ? 'Nu s-a putut număra. Încearcă din nou.'
      : numar.isLoading
        ? 'Se numără…'
        : n == null
          ? ''
          : `${n === 1 ? '1 activitate' : `${n.toLocaleString('ro-RO')} activități`} · ${numePerioada(de, pana)}`

  return (
    <Modal
      open
      onClose={onInchide}
      size="lg"
      title="Exportă Excel"
      footer={
        <>
          <span
            className={`mr-auto self-center text-mic ${intoarsa || numar.isError ? 'text-b4l-red' : 'text-b4l-text-muted'}`}
            aria-live="polite"
          >
            {rezumat}
          </span>
          <button
            type="button"
            onClick={onInchide}
            className="rounded-caseta px-3 py-1.5 text-corp text-b4l-text-muted hover:bg-b4l-bg"
          >
            Renunță
          </button>
          <button
            type="button"
            disabled={intoarsa || descarca || numar.isError || n === 0}
            onClick={() => void descarcaFisierul()}
            className="rounded-caseta bg-b4l-blue px-3.5 py-1.5 text-corp font-semibold text-white hover:opacity-90 disabled:opacity-40"
          >
            {descarca ? 'Se pregătește…' : 'Descarcă'}
          </button>
        </>
      }
    >
      <div className="grid gap-4">
        <p className="text-mic text-b4l-text-muted">
          Fișierul are banii și marfa fiecărei activități, ca pe ecran, plus raportul de închidere.
        </p>

        <fieldset className="grid gap-1.5">
          <legend className="mb-1.5 text-mic font-semibold text-b4l-text">Ce activități</legend>
          {CRITERII.map((c) => (
            <label
              key={c.cheie}
              className={`flex cursor-pointer items-start gap-2.5 rounded-lg border px-3 py-2 ${
                criteriu === c.cheie
                  ? 'border-b4l-blue bg-b4l-blue-light/40'
                  : 'border-b4l-border hover:bg-b4l-bg-soft'
              }`}
            >
              <input
                type="radio"
                name="criteriu-export"
                checked={criteriu === c.cheie}
                onChange={() => setCriteriu(c.cheie)}
                className="mt-1 accent-b4l-blue"
              />
              <span className="grid">
                <span className="text-corp text-b4l-text">{c.titlu}</span>
                <span className="text-mic text-b4l-text-muted">{c.detaliu}</span>
              </span>
            </label>
          ))}
        </fieldset>

        <div className="grid gap-1.5">
          <span className="text-mic font-semibold text-b4l-text">Perioada</span>
          <QuickDateRange
            fromValue={de}
            toValue={pana}
            onChange={(a, b) => {
              setDe(a)
              setPana(b)
            }}
          />
        </div>

        <label className="flex items-center gap-2 text-corp">
          <input
            type="checkbox"
            checked={anulate}
            onChange={(e) => setAnulate(e.target.checked)}
            className="accent-b4l-blue"
          />
          Include și activitățile anulate
        </label>
      </div>
    </Modal>
  )
}
